import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Logo } from '../common';
import Footer from '../common/Footer';
import useMetaTags from '../../hooks/useMetaTags';

const INVESTOR_TYPES = [
  'Individual',
  'Family office',
  'Impact fund',
  'Other'
];

const SUPPORTED_JURISDICTIONS = [
  'Japan',
  'Switzerland',
  'United Arab Emirates',
  'United Kingdom',
  'United States'
];

const MARKETS_OF_INTEREST = ['Kenya', 'India', 'Brazil', 'Mexico', 'Turkey', 'Bangladesh', 'South Africa', 'Egypt', 'Ethiopia'];

const ALLOCATION_RANGES = [
  'Under $10,000',
  '$10,000 – $100,000',
  '$100,000 – $1,000,000',
  'Above $1,000,000'
];

// Two-tone text formatting component — no bold, lightweight
const ForwardsflowText = ({ variant = 'default' }) => (
  <span className="font-sans font-normal inline-block">
    <span className={variant === 'white' ? 'text-white' : 'text-black'}>Forwards</span>
    <span className={variant === 'white' ? 'text-zinc-300' : 'text-zinc-500'}>flow</span>
  </span>
);

const formatBrandText = (text, variant = 'default') => {
  if (typeof text !== 'string') return text;
  const tokens = [];
  let currentIdx = 0;
  const regex = /(Forwardsflow)/gi;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > currentIdx) {
      tokens.push(text.substring(currentIdx, match.index));
    }
    tokens.push(<ForwardsflowText key={match.index} variant={variant} />);
    currentIdx = regex.lastIndex;
  }

  if (currentIdx < text.length) {
    tokens.push(text.substring(currentIdx));
  }
  return tokens.length > 0 ? tokens : text;
};

const ApplyPage = () => {
  useMetaTags({
    title: 'ForwardsFlow | Open Account',
    description: 'Open your cross-border cashflow management account with Forwardsflow. Access yield discovery, exchange rates, compliance, hedging, liquidity, execution and portfolio management across 6 markets.',
    canonical: 'https://www.forwardsflow.com/apply',
  });

  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    investorType: '',
    organizationName: '',
    country: '',
    contactName: '',
    jobTitle: '',
    email: '',
    phone: '',
    marketsOfInterest: [],
    instrumentPreference: '',
    estimatedAllocation: '',
    baseCurrency: '',
    fxHedging: false,
    additionalInfo: ''
  });

  const handleMarketToggle = (market) => {
    if (formData.marketsOfInterest.includes(market)) {
      setFormData({
        ...formData,
        marketsOfInterest: formData.marketsOfInterest.filter(m => m !== market)
      });
    } else {
      setFormData({
        ...formData,
        marketsOfInterest: [...formData.marketsOfInterest, market]
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Submitting investor application:', formData);
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white text-black font-sans antialiased">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            <Logo />
            <div className="hidden lg:flex items-center gap-6">
              <Link to="/" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Home</Link>
              <Link to="/pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Pricing</Link>
              <Link to="/demo" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Book a Demo</Link>
              <Link to="/contact" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Contact</Link>
              <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-medium">Platform Login</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {submitted ? (
            <div className="border border-zinc-300 p-10 text-center bg-white mt-12">
              <div className="w-12 h-12 border border-zinc-300 flex items-center justify-center mx-auto mb-6 bg-zinc-50">
                <CheckCircle className="w-6 h-6 text-black" />
              </div>
              <h3 className="text-xl font-bold uppercase tracking-wider text-black mb-3">Application Submitted</h3>
              <p className="text-xs font-mono text-zinc-600 leading-relaxed mb-6">
                Thank you for applying. Our onboarding team will verify your details and contact you within 48 hours to begin account opening and KYC processing.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium hover:bg-black transition-all"
              >
                Return Home <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div>
              <div className="text-center mb-10">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">Account Opening</span>
                <h1 className="text-3xl font-bold font-sans text-black">Open a T-bill investment account</h1>
                <p className="text-xs font-mono text-zinc-500 mt-2">Open a frontier fixed income investment account. Pre-KYC approval, bank-facilitated depository account setup.</p>
              </div>

              <div className="border border-zinc-300 p-8 bg-white">
                <form onSubmit={handleSubmit} className="space-y-6 font-mono text-xs text-black">
                  {/* Investor Type */}
                  <div>
                    <label className="block uppercase tracking-wider text-zinc-500 mb-2">Investor Type *</label>
                    <select
                      required
                      className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs bg-transparent focus:outline-none focus:border-black font-sans"
                      value={formData.investorType}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          investorType: val,
                          ...(val === 'Individual' ? { organizationName: '' } : {})
                        }));
                      }}
                    >
                      <option value="">Select investor type</option>
                      {INVESTOR_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {/* Organization & Country */}
                  <div className={`grid grid-cols-1 ${formData.investorType !== 'Individual' ? 'md:grid-cols-2' : ''} gap-4`}>
                    {formData.investorType !== 'Individual' && (
                      <div>
                        <label className="block uppercase tracking-wider text-zinc-500 mb-2">Organization Name *</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Acme Capital Partners"
                          className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                          value={formData.organizationName}
                          onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                        />
                      </div>
                    )}

                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Investor Jurisdiction *</label>
                      <select
                        required
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs bg-transparent focus:outline-none focus:border-black font-sans"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      >
                        <option value="">Select jurisdiction</option>
                        {SUPPORTED_JURISDICTIONS.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Contact Name & Job Title */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Contact Name *</label>
                      <input
                        type="text"
                        required
                        placeholder="First & Last Name"
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                        value={formData.contactName}
                        onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Job Title *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Portfolio Manager"
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                        value={formData.jobTitle}
                        onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Business Email *</label>
                      <input
                        type="email"
                        required
                        placeholder="name@yourfirm.com"
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        required
                        placeholder="e.g. +44 20 7946 0958"
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Markets of Interest */}
                  <div>
                    <label className="block uppercase tracking-wider text-zinc-500 mb-3">Markets of Interest</label>
                    <div className="flex flex-wrap gap-2">
                      {MARKETS_OF_INTEREST.map((market) => (
                        <button
                          key={market}
                          type="button"
                          onClick={() => handleMarketToggle(market)}
                          className={`px-3 py-1.5 border font-mono text-xs uppercase tracking-wider transition-all ${
                            formData.marketsOfInterest.includes(market)
                              ? 'border-[#4D4D4D] bg-[#4D4D4D] text-white'
                              : 'border-zinc-300 text-zinc-600 hover:border-[#4D4D4D]'
                          }`}
                        >
                          {market}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Instrument & Allocation */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Instrument Preference</label>
                      <select
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs bg-transparent focus:outline-none focus:border-black font-sans"
                        value={formData.instrumentPreference}
                        onChange={(e) => setFormData({ ...formData, instrumentPreference: e.target.value })}
                      >
                        <option value="">Select preference</option>
                        <option value="T-bills">91-day T-bills</option>
                        <option value="T-bills-FX">91-day T-bills with exchange rate forwards protection</option>
                      </select>
                    </div>

                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Estimated Initial Allocation (USD) *</label>
                      <select
                        required
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs bg-transparent focus:outline-none focus:border-black font-sans"
                        value={formData.estimatedAllocation}
                        onChange={(e) => setFormData({ ...formData, estimatedAllocation: e.target.value })}
                      >
                        <option value="">Select range</option>
                        {ALLOCATION_RANGES.map((range) => (
                          <option key={range} value={range}>{range}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Base Currency & Foreign Exchange Hedging */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Base Currency *</label>
                      <select
                        required
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs bg-transparent focus:outline-none focus:border-black font-sans"
                        value={formData.baseCurrency}
                        onChange={(e) => setFormData({ ...formData, baseCurrency: e.target.value })}
                      >
                        <option value="">Select currency</option>
                        <option value="USD">USD — US Dollar</option>
                        <option value="GBP">GBP — British Pound</option>
                        <option value="CHF">CHF — Swiss Franc</option>
                        <option value="AED">AED — UAE Dirham</option>
                        <option value="JPY">JPY — Japanese Yen</option>
                      </select>
                    </div>

                    <div className="flex items-end pb-1">
                      <div className="flex items-start gap-3 py-2">
                        <input
                          type="checkbox"
                          id="fx-hedging"
                          className="w-4 h-4 mt-0.5 border-zinc-300 text-black focus:ring-0 rounded-none"
                          checked={formData.fxHedging}
                          onChange={(e) => setFormData({ ...formData, fxHedging: e.target.checked })}
                        />
                        <label htmlFor="fx-hedging" className="text-zinc-600 font-sans cursor-pointer text-xs">
                          Interested in integrated foreign exchange hedging overlays
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div>
                    <label className="block uppercase tracking-wider text-zinc-500 mb-2">Additional Information</label>
                    <textarea
                      rows="3"
                      placeholder="Investment mandate details, specific requirements, or questions..."
                      className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                      value={formData.additionalInfo}
                      onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full py-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all"
                  >
                    Submit Application
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ApplyPage;
